
import priceCountryMixin from '@/mixins/priceCountryMixin'
import { schemaAvailability } from '@/utils/schemaAvailability'
import { productSharedBulbs } from '@/utils/sharedBulbCart'
import {
  schemaBrand,
  schemaIdentifiers,
} from '@/utils/productSchemaIdentifiers'

export default {
  mixins: [priceCountryMixin],
  props: {
    product: Object,
  },
  data() {
    return {
      returnDays: null,
    }
  },
  async fetch() {
    await this.$configurationService
      .getPublic('order_returns_max_delay')
      .then(({ data }) => {
        this.returnDays = parseInt(data.value)
      })
      .catch(() => {})
  },
  head() {
    return {
      __dangerouslyDisableSanitizers: ['script'],
      script: this.smartDataStructuredDataFromType().map(data => ({
        innerHTML: JSON.stringify(data),
        type: 'application/ld+json',
      })),
    }
  },
  methods: {
    getImagesFromProduct() {
      let images_arr = []
      if (
        this.product?.variant?.images &&
        this.product?.variant?.images?.length > 0
      ) {
        this.product?.variant?.images.forEach(item => {
          const url = item.url?.startsWith('http')
            ? item.url
            : `${this.$config.url}${item.url}`
          images_arr.push(url)
        })
      }
      return images_arr
    },
    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]*>/g, '')
    },
    getAvailability(variant) {
      return schemaAvailability(variant)
    },
    getReviews() {
      const reviews = this.product?.reviews
      if (!reviews?.length) return undefined
      return reviews
        .filter(review => review.stars)
        .map(review => ({
          '@type': 'Review',
          name: review.title || undefined,
          reviewBody: this.stripHtml(review.message),
          datePublished: review.published_at || review.created_at,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.stars,
            bestRating: 5,
            worstRating: 1,
          },
          author: {
            '@type': 'Person',
            name: review.user_fullname || review.user_firstname || 'Anonyme',
          },
        }))
    },
    getShippingDetails() {
      const range = this.product?.fastest_shipping_method_range
      const rangePrice = range?.price_ttc
      const value = typeof rangePrice === 'number' ? rangePrice : 0
      const details = {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: this.$options.filters.number(value, '0.00'),
          currency: 'EUR',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: this.priceCountry,
        },
      }

      if (typeof range?.delay === 'number' && range.delay > 0) {
        const transitDays = Math.ceil(range.delay / 24)
        details.deliveryTime = {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: 0,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: transitDays,
            maxValue: transitDays,
            unitCode: 'DAY',
          },
        }
      }

      return details
    },
    isPackProduct() {
      return this.product?.variant?.variant_type?.identifier === 'pack'
    },
    getOfferValidity() {
      const now = new Date()
      const nextYear = new Date(now)
      nextYear.setFullYear(nextYear.getFullYear() + 1)
      return {
        validFrom: now.toISOString().split('T')[0],
        priceValidUntil: nextYear.toISOString().split('T')[0],
      }
    },
    getVehicleYears(vehicle) {
      if (!vehicle?.year_from) return null
      if (!vehicle.year_to || vehicle.year_to === vehicle.year_from) {
        return `${vehicle.year_from}`
      }
      return `${vehicle.year_from} - ${vehicle.year_to}`
    },
    getVehicleName(vehicle) {
      const brand = vehicle?.brand?.name ? `${vehicle.brand.name} ` : ''
      return `${brand}${vehicle?.name || ''}`.trim()
    },
    getPackAdditionalProperties() {
      const props = []
      const sockets = this.product?.headlight_categories
        ?.map(item => item.name)
        .filter(Boolean)
      if (sockets?.length) {
        props.push({
          '@type': 'PropertyValue',
          name: 'Culot',
          value: sockets.join(', '),
        })
      }
      const functions = this.product?.headlight_types
        ?.map(item => item.name)
        .filter(Boolean)
      if (functions?.length) {
        props.push({
          '@type': 'PropertyValue',
          name: 'Fonction de feu',
          value: functions.join(', '),
        })
      }
      productSharedBulbs(this.product?.shared_bulbs).forEach(sharedBulb => {
        props.push({
          '@type': 'PropertyValue',
          name: this.$t('mixins.productMixin.dualFunction'),
          value: sharedBulb.label,
        })
      })
      const vehicles = this.product?.vehicles_included || []
      const names = vehicles.map(v => this.getVehicleName(v)).filter(Boolean)
      if (names.length) {
        props.push({
          '@type': 'PropertyValue',
          name: 'Véhicule compatible',
          value: names.join(', '),
        })
      }
      const years = [
        ...new Set(vehicles.map(v => this.getVehicleYears(v)).filter(Boolean)),
      ]
      if (years.length) {
        props.push({
          '@type': 'PropertyValue',
          name: 'Années',
          value: years.join(', '),
        })
      }
      return props.length ? props : undefined
    },
    getColorTemperatureProperties(variant) {
      if (!variant?.color_temperature) return []
      return [
        {
          '@type': 'PropertyValue',
          name: this.$t('pages.produit.slug.colorTemperature'),
          value: variant.color_temperature,
          unitCode: 'KEL',
          unitText: 'K',
        },
      ]
    },
    withAdditionalProperties(properties) {
      return properties.length ? { additionalProperty: properties } : {}
    },
    getPackVehicles() {
      const vehicles = this.product?.vehicles_included || []
      const nodes = vehicles
        .map(vehicle => {
          const name = this.getVehicleName(vehicle)
          if (!name) return null
          const node = { '@type': 'Vehicle', name }
          if (vehicle.brand?.name) {
            node.brand = { '@type': 'Brand', name: vehicle.brand.name }
          }
          const years = this.getVehicleYears(vehicle)
          if (years) node.vehicleModelDate = years
          return node
        })
        .filter(Boolean)
      return nodes.length ? nodes : undefined
    },
    getPackStructuredData() {
      if (!this.isPackProduct()) return {}
      const data = {}
      const reference = this.product?.variant?.reference
      if (reference) data.sku = reference
      const additionalProperty = this.getPackAdditionalProperties()
      if (additionalProperty) data.additionalProperty = additionalProperty
      const vehicles = this.getPackVehicles()
      if (vehicles) data.isAccessoryOrSparePartFor = vehicles
      return data
    },
    smartDataStructuredDataFromType() {
      const offerValidity = this.getOfferValidity()
      const packStructuredData = this.getPackStructuredData()
      const filtering =
        this.product.selectable_variants?.length > 1
          ? {
              productGroupID: this.product.id,
              hasVariant: this.product?.selectable_variants?.map(variant => ({
                '@type': 'Product',
                name: variant.name,
                description: this.stripHtml(variant.description_short),
                brand: schemaBrand(this.product),
                ...schemaIdentifiers(this.product, variant),
                isbn: variant.isbn,
                sku: this.$options.filters.number(variant.id),
                ...this.withAdditionalProperties(
                  this.getColorTemperatureProperties(variant)
                ),
                image: this.getImagesFromProduct(),
                url: this.$config.url + this.$route.path,
                offers: {
                  '@type': 'Offer',
                  url: `${this.$config.url}${this.localePath({
                    name: 'produit-slug',
                    params: { slug: this.product.slug },
                    query: { variant: variant.id },
                  })}`,
                  validFrom: offerValidity.validFrom,
                  priceValidUntil: offerValidity.priceValidUntil,
                  price: this.$options.filters.number(
                    variant.price_ttc_discount,
                    '0.00'
                  ),
                  availability: this.getAvailability(variant),
                  priceCurrency: 'EUR',
                  itemCondition: 'https://schema.org/NewCondition',
                  ...this.getReturnPolicyLink(),
                  shippingDetails: this.getShippingDetails(),
                },
              })),
              '@type': 'ProductGroup',
            }
          : {
              '@type': 'Product',
              offers: {
                url: `${this.$config.url}${this.localePath({
                  name: 'produit-slug',
                  params: { slug: this.product.slug },
                })}`,
                '@type': 'Offer',
                validFrom: offerValidity.validFrom,
                priceValidUntil: offerValidity.priceValidUntil,
                price: this.$options.filters.number(
                  this.product.variant?.price_ttc_discount,
                  '0.00'
                ),
                availability: this.getAvailability(this.product.variant),
                priceCurrency: 'EUR',
                itemCondition: 'https://schema.org/NewCondition',
                ...this.getReturnPolicyLink(),
                shippingDetails: this.getShippingDetails(),
                seller: {
                  '@type': 'Organization',
                  name: 'AGM Vision',
                },
              },
              image: this.getImagesFromProduct(),
              ...schemaIdentifiers(this.product, this.product?.variant),
              isbn: this.product?.variant.isbn,
              sku: this.$options.filters.number(this.product.id),
            }

      return [
        {
          '@context': 'https://schema.org',
          brand: schemaBrand(this.product),
          aggregateRating:
            this.product.reviews_count > 0
              ? {
                  '@type': 'AggregateRating',
                  ratingValue: this.product.structured_data.ratingValue,
                  ratingCount: this.product.structured_data.ratingCount,
                  reviewCount: this.product.structured_data.ratingCount,
                  bestRating: this.product.structured_data.bestRating,
                  worstRating: this.product.structured_data.worstRating,
                }
              : null,
          review: this.getReviews(),
          name: this.product.name,
          category: this.product.vehicles_included?.length
            ? `LED pour ${this.product.vehicles_included[0].brand.name} ${this.product.vehicles_included[0].name}`
            : this.product?.category?.length
            ? this.product.category[0].name
            : 'LED',
          description: this.stripHtml(this.product?.variant.description_short),
          url: this.$config.url + this.$route.path,
          ...filtering,
          ...packStructuredData,
          ...this.withAdditionalProperties([
            ...(packStructuredData.additionalProperty || []),
            ...(this.product.selectable_variants?.length > 1
              ? []
              : this.getColorTemperatureProperties(this.product.variant)),
          ]),
        },
        this.getReturnPolicy(),
      ].filter(Boolean)
    },
    getReturnPolicyLink() {
      return this.returnDays
        ? { hasMerchantReturnPolicy: { '@id': '#return_policy' } }
        : {}
    },
    getReturnPolicy() {
      if (!this.returnDays) return null
      return {
        '@context': 'https://schema.org/',
        '@type': 'MerchantReturnPolicy',
        '@id': '#return_policy',
        applicableCountry: this.priceCountry,
        returnPolicyCategory:
          'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: this.returnDays,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      }
    },
  },
}
